import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, UserRound } from "lucide-react";
import SEO from "@/components/SEO";
import { absUrl } from "@/lib/seo";
import FeatureVisual from "@/components/features/FeatureVisual";
import { trackPublicFeatureEvent } from "@/lib/publicFeatureEvents";

const audiences = [
  {
    key: "renter",
    icon: UserRound,
    title: "For renters",
    description: "Review what was recorded, request corrections, follow the booking, and keep a customer-visible rental history.",
    to: "/features/renters",
    cta: "Explore renter features",
    points: ["No account required for secure confirmation links", "Signed-in My Bookings history", "Authorized self-drive drivers and material-change acceptance", "Release, return and post-rental records", "Booking-linked reviews"],
  },
  {
    key: "business",
    icon: Building2,
    title: "For rental businesses",
    description: "Move from scattered messages to a connected workflow for bookings, vehicles, handovers, incidents, customers, and staff, plus Legal Readiness, drivers and dispatch, Finance & Books, and return, damage and settlement.",
    to: "/features/businesses",
    cta: "Explore business features",
    points: ["Structured booking intake", "Conflict and readiness checks", "Business and fleet Legal Readiness", "Drivers, dispatch and authorized self-drive drivers", "Finance & Books", "Operational history and accountability"],
  },
];

export default function Features() {
  const canonicalUrl = absUrl("/features");
  useEffect(() => {
    trackPublicFeatureEvent("public_feature_page_viewed", { audience: "overview" });
  }, []);

  return (
    <>
      <SEO
        title="ConfirmPH Features for Renters and Rental Businesses"
        description="See how ConfirmPH connects rental booking confirmation, vehicle operations, customer records, handovers, incidents, rental closure, and booking-linked reviews."
        canonical="/features"
        og={{ type: "website", title: "ConfirmPH Features", description: "One connected rental record for renters and rental businesses.", url: canonicalUrl }}
        jsonLd={[{ "@context": "https://schema.org", "@type": "WebPage", name: "ConfirmPH Features", url: canonicalUrl }]}
      />

      <header className="overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_20%_0%,_rgba(167,243,208,0.5),_transparent_40%),linear-gradient(145deg,#f8fbf9,#fff)]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-800">Product features</p>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-6xl">One rental. One connected process.</h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">
              ConfirmPH connects the renter-facing booking record with the business workflow behind it, from the first inquiry through vehicle return and review.
            </p>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <p className="mb-8 max-w-4xl text-sm leading-7 text-slate-600" data-testid="features-lifecycle">
            <span className="font-semibold text-slate-900">The full rental: </span>
            inquiry → agreement and confirmation → release → active rental → return and inspection →
            settlement and closure, with damage, claims or disputes when they are needed.
          </p>
          <div className="grid gap-6 lg:grid-cols-2">
            {audiences.map((audience) => {
              const Icon = audience.icon;
              return (
                <article key={audience.key} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-800"><Icon className="h-6 w-6" aria-hidden="true" /></span>
                  <h2 className="mt-5 font-display text-3xl font-bold text-slate-950">{audience.title}</h2>
                  <p className="mt-3 leading-7 text-slate-600">{audience.description}</p>
                  <ul className="mt-5 space-y-2">
                    {audience.points.map((point) => <li key={point} className="flex items-start gap-2 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" aria-hidden="true" />{point}</li>)}
                  </ul>
                  <Link
                    to={audience.to}
                    onClick={() => trackPublicFeatureEvent("public_feature_audience_selected", { audience: audience.key })}
                    className="mt-7 inline-flex min-h-11 items-center gap-2 rounded-lg bg-[#1B4332] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#143027]"
                  >
                    {audience.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </article>
              );
            })}
          </div>
        </section>

        <section className="border-y border-slate-200 bg-[#F6F8F7]">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-800">Real product screens</p>
              <h2 className="mt-3 font-display text-3xl font-bold text-slate-950 sm:text-4xl">See the workflow, not a fabricated dashboard.</h2>
              <p className="mt-4 leading-7 text-slate-600">These visuals were captured from ConfirmPH's running public demo flows with synthetic data. Open any image for a closer look.</p>
            </div>
            <div className="mt-9 grid gap-6 lg:grid-cols-2">
              <FeatureVisual image="/images/features/renter-confirmation.webp" alt="Renter confirmation page in ConfirmPH" caption="The renter reviews the same record the business created." page="overview" featureId="renter-proof" priority />
              <FeatureVisual image="/images/features/business-pickup-handover.webp" alt="Business pickup handover in ConfirmPH" caption="The business records the handover. The vehicle is released only when the applicable readiness checks also pass." page="overview" featureId="business-proof" priority />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="grid gap-8 rounded-3xl bg-slate-950 px-6 py-10 text-white sm:px-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Try the full flow</p>
              <h2 className="mt-3 font-display text-3xl font-bold">Walk through common rental situations step by step.</h2>
              <p className="mt-3 max-w-2xl leading-7 text-slate-300">The demos are sandboxed. Nothing you do there creates a real booking or changes business data.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/demo-business/scenarios" onClick={() => trackPublicFeatureEvent("public_feature_cta_clicked", { audience: "overview", destination: "/demo-business/scenarios" })} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-slate-100">
                Open interactive demos <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
